/**
 * @ComponentFor SearchPageViewModel
 */

import React, { useState, useEffect } from 'react';
import connect from 'Shared/connect';
import { currentUrl, equalsUrl, URLX } from '@polarnopyret/scope/index';
import { styled } from '@glitz/react';
import { get, replaceState } from '@polarnopyret/scope';
import { EpiProperty, epiPropertyValue } from '@polarnopyret/scope-episerver';
import {PageType} from 'Shared/State';
import ProductListing from 'Product/ProductListing';
import CmsPageListing from 'Search/Listing/CmsPageListing';
import SearchPageViewModelType from './SearchPageViewModel.type';
import Page from 'Shared/Page';
import {isCompact} from 'Shared/Viewport';
import SearchPageTop from './SearchPageTop';
import ProductListViewModel from 'Product/ProductListing/ProductListViewModel.type';
import ProductListItemViewModel from 'Product/ProductListing/ProductListItemViewModel.type';
import CmsPageListViewModel from './CmsPageListViewModel.type';
import { SEARCH_QUERY_NAME } from 'Search/action-creators';

export type AlignmentStringType = 'left' | 'right' | 'center';

export type SearchGroup = {
  type: string;
  translation: string;
  enabled: boolean;
};

type ConnectedPropType = {
  currentBreakpoint: number;
};

type RequiredPropType = SearchPageViewModelType & PageType;
type PropType = RequiredPropType & ConnectedPropType;

const ContentAreaWrap = styled.div({
  marginBottom: '40px',
  marginTop: '40px'
})

const SearchPage = connect(
  (state) => ({
    currentBreakpoint: state.currentBreakpoint,
  }),
)(
  (props: PropType) => { 
    const {
      originalSearchPhrase,
      page,
      currentBreakpoint,
      labels = {},
    } = props;

    const isMobile = isCompact(currentBreakpoint);
        
    const [productResult, setProductResult] = useState<ProductListViewModel>(props.productList);
    const [cmsResult, setCmsResult] = useState<CmsPageListViewModel>(props.cmsList);
    const [recommendations, setRecommendations] = useState<ProductListItemViewModel[]>(props.recommendations);
    const [searchPhrase, setSearchPhrase] = useState<string>(originalSearchPhrase);

    const getSearchGroups = () => {
      return [
        { type: 'product', translation: props.labels.products, enabled: (productResult?.totalItemCount ?? 0) > 0 },
        { type: 'cmspage', translation: props.labels.pages, enabled: (cmsResult?.cmsPages?.length ?? 0) > 0 },
      ]
    }

    const searchGrops = getSearchGroups();
    const [tabGroups, setSearchGroups] = useState<SearchGroup[]>(searchGrops);
    const [selectedGroup, setSelectedGroup] = useState<SearchGroup>(searchGrops.find(x => x.enabled));
    
    useEffect(() => {
      if(searchPhrase !== originalSearchPhrase) {
        setSearchPhrase(originalSearchPhrase);
        setCmsResult(props.cmsList);
        setProductResult(props.productList);
        setRecommendations(props.recommendations);
      }
    }, [originalSearchPhrase]);
    
    useEffect(() => {
      const groups = getSearchGroups();
      setSearchGroups(groups);
      setSelectedGroup(groups.find(x => x.enabled))
    }, [cmsResult, productResult]);

    const getQueryParams = () => {
      const url = currentUrl();
      return url.searchParams.get(SEARCH_QUERY_NAME);
    }

    const searchUrl = (searchText: string) => {
      const url = new URLX(currentUrl());
      url.searchParams.set(SEARCH_QUERY_NAME, searchText);
      return url;
    };

    const search = (query: string) => {
      if (query.length) {
        const newUrl = searchUrl(query);
        if (!equalsUrl(currentUrl(), newUrl)) {
          const displayUrl = newUrl.toString();
          newUrl.pathname = newUrl.pathname + '/search';
          
          get(newUrl, {})
            .then((r) => r.json())
            .then((json: SearchPageViewModelType) => {
              setProductResult(json.productList);
              setCmsResult(json.cmsList);
              setRecommendations(json.recommendations);
              replaceState(displayUrl, null, false);
            });
        }
      }
    }

    const selectGroup = (newSelectedGroup: SearchGroup) => {
      setSelectedGroup(newSelectedGroup);
    }

    const setFilterResult = (newResult: ProductListViewModel) => {
      setProductResult(newResult);
    }

    const productListProps: PropType = { ...props, productList: productResult };
    const searchCmsPage = cmsResult?.cmsPages || [];    
    const totalResultCount = (productResult?.totalItemCount ?? 0) + searchCmsPage.length;

    return (
      <Page>
        <h1 className='visually-hidden'>{epiPropertyValue(page.name)}</h1>
        <SearchPageTop
          totalResultCount={totalResultCount}
          searchGroupsListFiltered={tabGroups}
          selectedGroup={selectedGroup}
          isMobile={isMobile}
          searchPhrase={searchPhrase}
          labels={labels}
          selectGroup={(group: SearchGroup) => selectGroup(group)}
          executeSearch={search}
          setSearchPhrase={setSearchPhrase}
        />
      
        {(productResult?.totalItemCount ?? 0) > 0 && selectedGroup?.type === 'product' && (
          <ProductListing {...productListProps} queryParams={getQueryParams()} setFilterResults={(result: ProductListViewModel) => setFilterResult(result)} />
        )}
        {recommendations && recommendations.length > 0 && (
          <ProductListing hideFacetAndSort={true} productList={{products: recommendations, facets: null, sortOptions: null, listContentItems: null, currentPageIndex: 0, labels: null, totalItemCount: props.recommendations?.length ?? 0, totalPages: 1}} isLoading={false} />        )}
        {(cmsResult?.cmsPages.length ?? 0) > 0 && selectedGroup?.type === 'cmspage' && (
          <CmsPageListing
            breakPoint={currentBreakpoint}
            uppercase={true}
            cmsPageList={cmsResult}
            isMobile={isMobile}
          />
        )}
        
        <ContentAreaWrap>
          <EpiProperty for={page.contentArea} />
        </ContentAreaWrap>
      </Page>
    );
  }
)

export default SearchPage;
